import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Cancel as CancelIcon,
  EventNote as EventNoteIcon,
  AccessTime as AccessTimeIcon,
  ArrowBack as ArrowBackIcon,
  MenuBook,
  Menu as MenuIcon
} from '@mui/icons-material';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import ThemeBackground from '../Layout/ThemeBackground';
import MenuDrawer from '../Mobile/MenuDrawer';
import BottomNav from '../Mobile/BottomNav';

interface Appointment {
  _id: string;
  clientId: {
    _id: string;
    firstName: string;
    lastName: string;
  };
  counsellorId: string;
  scheduledStart: string;
  scheduledEnd: string;
  status: 'scheduled' | 'completed' | 'cancelled' | 'no_show';
  reminderStatus: 'pending' | 'sent' | 'failed';
  linkedSessionNoteId?: string;
  cancellationReason?: string;
  createdAt: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`appointment-tabpanel-${index}`}
      aria-labelledby={`appointment-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const AppointmentDashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCreatingAppointment, setIsCreatingAppointment] = useState(false);
  const [clients, setClients] = useState<any[]>([]);
  const [bookedClientIds, setBookedClientIds] = useState<Set<string>>(new Set());
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({ open: false, message: '', severity: 'success' });

  const [newAppointment, setNewAppointment] = useState({
    clientId: '',
    scheduledStart: new Date()
  });
  // CRITICAL: counsellorProfileId is the Counsellor document _id (from Counsellor collection)
  // This is different from user._id which is the User document _id (from User collection)
  // Appointments require the Counsellor document ID, not the User ID
  const [counsellorProfileId, setCounsellorProfileId] = useState<string>('');

  const fetchAppointments = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/appointments/counsellor/me');
      setAppointments(response.data);
    } catch (error) {
      console.error('Error fetching appointments:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load appointments',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, []);

  // Which clients already hold a 'scheduled' appointment (any counsellor).
  // Server-side truth - the local `appointments` list only covers the
  // caller's own diary, which for admins is empty.
  const fetchBookedClients = useCallback(async () => {
    try {
      const response = await api.get('/api/appointments/booked-client-ids');
      setBookedClientIds(new Set(
        (response.data?.clientIds || []).map((id: any) => id.toString())
      ));
    } catch (error) {
      console.error('Error fetching booked clients:', error);
      setBookedClientIds(new Set());
    }
  }, []);

  const fetchClients = useCallback(async () => {
    try {
      // E2E-02 (Phase 5): /api/counsellors/clients is counsellor-scoped and
      // 403s for administrators. Admins list all active clients via
      // /api/clients instead.
      const response = user?.role === 'administrator'
        ? await api.get('/api/clients')
        : await api.get('/api/counsellors/clients');
      setClients(response.data.clients || response.data || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
      setClients([]);
    }
  }, [user]);

  // E2E-02 (Phase 5): admins book on behalf of a chosen counsellor - the
  // previous fallback to the admin's own user._id silently misattributed
  // the appointment. Bookable = not archived/inactive/suspended.
  const [counsellors, setCounsellors] = useState<any[]>([]);
  const [selectedCounsellorId, setSelectedCounsellorId] = useState<string>('');

  const fetchCounsellors = useCallback(async () => {
    if (user?.role !== 'administrator') return;
    try {
      const response = await api.get('/api/counsellors');
      const list = Array.isArray(response.data) ? response.data : (response.data?.counsellors || []);
      setCounsellors(list.filter((c: any) =>
        !c.isArchived && c.status !== 'inactive' && c.applicationStatus !== 'Suspended'
      ));
    } catch (error) {
      console.error('Error fetching counsellors:', error);
      setCounsellors([]);
    }
  }, [user]);

  const fetchCounsellorProfile = useCallback(async () => {
    try {
      if (user?.role === 'counsellor') {
        // CRITICAL: Fetch the Counsellor document ID (not the User document ID)
        // user._id = User collection document ID
        // response.data._id = Counsellor collection document ID
        // We need the Counsellor ID for creating appointments
        const response = await api.get('/api/counsellors/profile');
        setCounsellorProfileId(response.data._id); // Store Counsellor document _id
      }
    } catch (error) {
      console.error('Error fetching counsellor profile:', error);
    }
  }, [user]);

  useEffect(() => {
    fetchAppointments();
    fetchClients();
    fetchBookedClients();
    fetchCounsellorProfile();
    fetchCounsellors();
  }, [fetchAppointments, fetchClients, fetchBookedClients, fetchCounsellorProfile, fetchCounsellors]);

  // Filter clients to only show those without a scheduled appointment.
  // Uses the server-side booked set (covers every counsellor, and works for
  // admins whose own appointments list is empty).
  const availableClients = clients.filter(
    client => !bookedClientIds.has(client._id?.toString())
  );

  const handleCreateAppointment = async () => {
    try {
      if (!newAppointment.clientId) {
        setSnackbar({
          open: true,
          message: 'Please select a client',
          severity: 'warning'
        });
        return;
      }

      // Calculate end time (1 hour after start)
      const scheduledEnd = new Date(newAppointment.scheduledStart.getTime() + 60 * 60000);

      // Check for overlapping appointments
      const hasOverlap = appointments.some(apt => {
        // Ignore all cancelled appointments (cancelled, cancelled_by_counsellor, cancelled_by_client)
        if (apt.status.includes('cancelled')) return false;
        
        const aptStart = new Date(apt.scheduledStart);
        const aptEnd = new Date(apt.scheduledEnd);
        const newStart = newAppointment.scheduledStart;
        const newEnd = scheduledEnd;

        // Check if times overlap
        return (newStart < aptEnd && newEnd > aptStart);
      });

      if (hasOverlap) {
        setSnackbar({
          open: true,
          message: 'This time slot overlaps with an existing appointment. Please choose a different time.',
          severity: 'error'
        });
        return;
      }

      // CRITICAL: counsellorId must be the Counsellor document ID, not User ID
      // - For counsellors: use counsellorProfileId (Counsellor collection _id)
      // - For admins: must pick from the counsellor list - never the admin's
      //   own user._id (that stored a User ID and misattributed the booking)
      const effectiveCounsellorId = user?.role === 'administrator'
        ? selectedCounsellorId
        : counsellorProfileId;

      if (!effectiveCounsellorId) {
        setSnackbar({
          open: true,
          message: 'Please select a counsellor for this appointment',
          severity: 'warning'
        });
        return;
      }

      const response = await api.post('/api/appointments', {
        clientId: newAppointment.clientId,
        counsellorId: effectiveCounsellorId,
        scheduledStart: newAppointment.scheduledStart.toISOString(),
        scheduledEnd: scheduledEnd.toISOString(),
        status: 'scheduled',
        reminderStatus: 'pending'
      });

      // CAL-02 (Phase 5): availability is advisory - the booking is still
      // created, but surface the server's flag when it falls outside the
      // counsellor's declared availability.
      setSnackbar({
        open: true,
        message: response.data?.outsideAvailability
          ? 'Appointment created - note this falls outside the counsellor\'s declared availability'
          : 'Appointment created successfully',
        severity: response.data?.outsideAvailability ? 'warning' : 'success'
      });

      setIsCreatingAppointment(false);
      setNewAppointment({
        clientId: '',
        scheduledStart: new Date()
      });
      setSelectedCounsellorId('');
      fetchAppointments();
      fetchBookedClients();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to create appointment';
      const existingAppointment = error.response?.data?.existingAppointment;
      
      if (existingAppointment) {
        const existingDate = new Date(existingAppointment.scheduledStart).toLocaleString();
        setSnackbar({
          open: true,
          message: `This client already has a scheduled appointment on ${existingDate}. Please cancel or reschedule the existing appointment first.`,
          severity: 'warning'
        });
      } else {
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: 'error'
        });
      }
    }
  };

  const handleCancelAppointment = async (appointmentId: string) => {
    if (!window.confirm('Are you sure you want to cancel this appointment?')) {
      return;
    }

    try {
      await api.put(`/api/appointments/${appointmentId}/cancel`, {
        reason: 'Cancelled by counsellor'
      });

      setSnackbar({
        open: true,
        message: 'Appointment cancelled',
        severity: 'success'
      });

      fetchAppointments();
      fetchBookedClients();
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to cancel appointment',
        severity: 'error'
      });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled':
        return 'primary';
      case 'completed':
        return 'success';
      case 'cancelled':
        return 'error';
      case 'no_show':
        return 'warning';
      default:
        return 'default';
    }
  };

  const now = new Date();
  const scheduledAppointments = appointments.filter(apt => 
    apt.status === 'scheduled' && new Date(apt.scheduledStart) > now
  );
  // Notes Due: Past scheduled appointments (session happened but not marked complete) 
  // OR completed appointments without session notes
  const notesDueAppointments = appointments.filter(apt => 
    (apt.status === 'scheduled' && new Date(apt.scheduledStart) <= now) ||
    (apt.status === 'completed' && !apt.linkedSessionNoteId)
  );
  const completedAppointments = appointments.filter(
    apt => apt.status === 'completed' && apt.linkedSessionNoteId
  );

  const renderAppointmentCard = (appointment: Appointment, showActions: boolean = true) => (
    <Card
      key={appointment._id}
      onClick={() => {
        if (appointment.status === 'scheduled') {
          navigate(`/client-details/${appointment.clientId._id}`);
        }
      }}
      sx={{
        mb: 2,
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(10px)',
        borderRadius: '12px',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        transition: 'all 0.2s ease',
        cursor: appointment.status === 'scheduled' ? 'pointer' : 'default',
        '&:hover': {
          transform: appointment.status === 'scheduled' ? 'translateY(-2px)' : 'none',
          boxShadow: appointment.status === 'scheduled' ? '0 6px 20px rgba(102, 126, 234, 0.3)' : 'none',
          borderColor: appointment.status === 'scheduled' ? 'rgba(102, 126, 234, 0.4)' : 'rgba(255, 255, 255, 0.1)'
        }
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="h6" className="theme-text-primary" sx={{ fontWeight: 700, mb: 0.5 }}>
              {appointment.clientId.firstName} {appointment.clientId.lastName}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <AccessTimeIcon sx={{ fontSize: 16, color: 'rgba(255, 255, 255, 0.6)' }} />
              <Typography variant="body2" className="theme-text-secondary">
                {new Date(appointment.scheduledStart).toLocaleString('en-GB', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </Typography>
            </Box>
            <Chip
              label={appointment.status.replace('_', ' ').toUpperCase()}
              color={getStatusColor(appointment.status) as any}
              size="small"
              sx={{ fontWeight: 600 }}
            />
          </Box>
          {showActions && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              {appointment.status === 'scheduled' && (
                <>
                  <IconButton
                    size="small"
                    onClick={() => handleCancelAppointment(appointment._id)}
                    sx={{ color: 'rgba(244, 67, 54, 0.8)' }}
                  >
                    <CancelIcon />
                  </IconButton>
                </>
              )}
              {appointment.status === 'completed' && !appointment.linkedSessionNoteId && (
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<EventNoteIcon />}
                  href={`/client-details/${appointment.clientId._id}/sessions`}
                  sx={{
                    textTransform: 'none',
                    backgroundColor: '#667eea',
                    '&:hover': { backgroundColor: '#5568d3' }
                  }}
                >
                  Add Note
                </Button>
              )}
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <ThemeBackground variant="full">
      {/* Mobile Header */}
      <Box sx={{ display: { xs: 'block', md: 'none' }, pb: '80px' }}>
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 1000,
            background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            px: 2,
            py: 1.5
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography
              variant="h6"
              sx={{
                color: 'white',
                fontWeight: 600,
                fontSize: '1.25rem'
              }}
            >
              Appointments
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton
                onClick={() => setIsCreatingAppointment(true)}
                sx={{
                  color: 'white',
                  background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
                  width: 40,
                  height: 40,
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                  }
                }}
              >
                <AddIcon />
              </IconButton>
              <IconButton
                onClick={() => setMobileMenuOpen(true)}
                sx={{
                  color: 'white',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  width: 40,
                  height: 40,
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5568d3 0%, #6a4293 100%)',
                  }
                }}
              >
                <MenuIcon />
              </IconButton>
            </Box>
          </Box>
        </Box>

        {/* Mobile Tabs */}
        <Box sx={{ px: 2, py: 2, background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)' }}>
          <Tabs
            value={tabValue}
            onChange={(e, newValue) => setTabValue(newValue)}
            variant="fullWidth"
            sx={{
              '& .MuiTab-root': {
                color: 'rgba(255, 255, 255, 0.6)',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.875rem',
                minHeight: 48
              },
              '& .Mui-selected': {
                color: '#667eea !important'
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#667eea'
              }
            }}
          >
            <Tab label={`Scheduled (${scheduledAppointments.length})`} />
            <Tab label={`Notes Due (${notesDueAppointments.length})`} />
            <Tab label="Completed" />
          </Tabs>
        </Box>

        {/* Mobile Content */}
        <Box sx={{ px: 2, py: 2 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TabPanel value={tabValue} index={0}>
                {scheduledAppointments.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 8, color: 'rgba(255, 255, 255, 0.5)' }}>
                    <Typography variant="body1">No scheduled appointments</Typography>
                  </Box>
                ) : (
                  scheduledAppointments.map(apt => renderAppointmentCard(apt))
                )}
              </TabPanel>
              <TabPanel value={tabValue} index={1}>
                {notesDueAppointments.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 8, color: 'rgba(255, 255, 255, 0.5)' }}>
                    <Typography variant="body1">No notes due</Typography>
                  </Box>
                ) : (
                  notesDueAppointments.map(apt => renderAppointmentCard(apt))
                )}
              </TabPanel>
              <TabPanel value={tabValue} index={2}>
                {completedAppointments.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 8, color: 'rgba(255, 255, 255, 0.5)' }}>
                    <Typography variant="body1">No completed appointments</Typography>
                  </Box>
                ) : (
                  completedAppointments.map(apt => renderAppointmentCard(apt, false))
                )}
              </TabPanel>
            </>
          )}
        </Box>

        {/* Mobile Menu Drawer */}
        <MenuDrawer
          open={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
          counsellorId={user?.counsellorId}
        />

        {/* Bottom Navigation */}
        <BottomNav counsellorId={user?.counsellorId} />
      </Box>

      {/* Desktop View */}
      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
      <div className="container mx-auto px-2 sm:px-4 py-4 sm:py-6 overflow-y-auto max-h-screen">
        <div className="max-w-7xl mx-auto">
          {/* Header Section */}
          <div className="glass-card p-4 sm:p-6 mb-4 sm:mb-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <IconButton
                    onClick={() => navigate('/counsellor/dashboard')}
                    sx={{
                      color: 'rgba(255, 255, 255, 0.8)',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        color: '#667eea'
                      }
                    }}
                  >
                    <ArrowBackIcon />
                  </IconButton>
                  <Typography 
                    variant="h4" 
                    component="h1" 
                    className="theme-text-primary"
                    sx={{
                      fontWeight: 700,
                      fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' }
                    }}
                  >
                    My Appointments
                  </Typography>
                </Box>
                <Typography 
                  variant="subtitle1"
                  className="theme-text-secondary"
                  sx={{
                    fontWeight: 400,
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    ml: 7
                  }}
                >
                  Manage your client sessions and notes
                </Typography>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
                <Button
                  variant="contained"
                  onClick={() => navigate('/counsellor/policies')}
                  startIcon={<MenuBook />}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    py: 1,
                    minWidth: { sm: '200px' },
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    boxShadow: '0 4px 15px rgba(245, 158, 11, 0.4)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
                      boxShadow: '0 6px 20px rgba(245, 158, 11, 0.6)'
                    }
                  }}
                >
                  Policies & Procedures
                </Button>
                <Button
                  variant="contained"
                  onClick={() => navigate('/settings')}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    py: 1,
                    minWidth: { sm: '200px' },
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    boxShadow: '0 4px 15px rgba(16, 185, 129, 0.4)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                      boxShadow: '0 6px 20px rgba(16, 185, 129, 0.6)'
                    }
                  }}
                >
                  Security
                </Button>
                <Button
                  variant="outlined"
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    py: 1,
                    minWidth: { sm: '120px' },
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                    color: 'rgba(255, 255, 255, 0.9)',
                    '&:hover': {
                      borderColor: 'rgba(255, 255, 255, 0.5)',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)'
                    }
                  }}
                  onClick={handleLogout}
                >
                  Logout
                </Button>
              </div>
            </div>
          </div>

          {/* Appointments Content */}
          <Box sx={{ p: 3 }} className="glass-card">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" className="theme-text-primary" sx={{ fontWeight: 700 }}>
            Schedule & Sessions
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setIsCreatingAppointment(true)}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              backgroundColor: '#2196f3',
              '&:hover': { backgroundColor: '#1976d2' }
            }}
          >
            Schedule Appointment
          </Button>
        </Box>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs
            value={tabValue}
            onChange={(e, newValue) => setTabValue(newValue)}
            sx={{
              '& .MuiTab-root': {
                color: 'rgba(255, 255, 255, 0.6)',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '1rem'
              },
              '& .Mui-selected': {
                color: '#667eea !important'
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#667eea'
              }
            }}
          >
            <Tab label={`Scheduled (${scheduledAppointments.length})`} />
            <Tab label={`Notes Due (${notesDueAppointments.length})`} />
            <Tab label={`Completed (${completedAppointments.length})`} />
          </Tabs>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TabPanel value={tabValue} index={0}>
              {scheduledAppointments.length === 0 ? (
                <Typography className="theme-text-secondary" sx={{ textAlign: 'center', py: 4 }}>
                  No scheduled appointments
                </Typography>
              ) : (
                scheduledAppointments.map(apt => renderAppointmentCard(apt))
              )}
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              {notesDueAppointments.length === 0 ? (
                <Typography className="theme-text-secondary" sx={{ textAlign: 'center', py: 4 }}>
                  No session notes due
                </Typography>
              ) : (
                notesDueAppointments.map(apt => renderAppointmentCard(apt))
              )}
            </TabPanel>

            <TabPanel value={tabValue} index={2}>
              {completedAppointments.length === 0 ? (
                <Typography className="theme-text-secondary" sx={{ textAlign: 'center', py: 4 }}>
                  No completed appointments
                </Typography>
              ) : (
                completedAppointments.map(apt => renderAppointmentCard(apt, false))
              )}
            </TabPanel>
          </>
        )}
      </Box>

      {/* Create Appointment Dialog */}
      <Dialog
        open={isCreatingAppointment}
        onClose={() => setIsCreatingAppointment(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: 'linear-gradient(135deg, rgba(30, 30, 46, 0.95) 0%, rgba(24, 24, 37, 0.95) 100%)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }
        }}
      >
        <DialogTitle>
          <Typography variant="h6" className="theme-text-primary" sx={{ fontWeight: 700 }}>
            Schedule New Appointment
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {user?.role === 'administrator' && (
              <Grid item xs={12}>
                <label className="block text-sm font-medium theme-text-secondary mb-2">Counsellor *</label>
                <TextField
                  select
                  fullWidth
                  value={selectedCounsellorId}
                  onChange={(e) => setSelectedCounsellorId(e.target.value)}
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: 'rgba(255, 255, 255, 0.9)',
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                      '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                      '&.Mui-focused fieldset': { borderColor: '#667eea' }
                    },
                    '& .MuiSelect-select': {
                      color: 'rgba(255, 255, 255, 0.9)'
                    }
                  }}
                  SelectProps={{
                    MenuProps: {
                      PaperProps: {
                        sx: {
                          bgcolor: 'rgba(30, 30, 46, 0.98)',
                          backdropFilter: 'blur(20px)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          '& .MuiMenuItem-root': {
                            color: 'rgba(255, 255, 255, 0.9)',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.1)'
                            },
                            '&.Mui-selected': {
                              backgroundColor: 'rgba(102, 126, 234, 0.3)',
                              '&:hover': {
                                backgroundColor: 'rgba(102, 126, 234, 0.4)'
                              }
                            }
                          }
                        }
                      }
                    }
                  }}
                >
                  <MenuItem value="">
                    {counsellors.length === 0
                      ? '-- No bookable counsellors --'
                      : '-- Select a counsellor --'}
                  </MenuItem>
                  {counsellors.map((counsellor) => (
                    <MenuItem key={counsellor._id} value={counsellor._id}>
                      {counsellor.firstName} {counsellor.lastName}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
            <Grid item xs={12}>
              <label className="block text-sm font-medium theme-text-secondary mb-2">Client *</label>
              <TextField
                select
                fullWidth
                value={newAppointment.clientId}
                onChange={(e) => setNewAppointment({ ...newAppointment, clientId: e.target.value })}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'rgba(255, 255, 255, 0.9)',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                    '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                    '&.Mui-focused fieldset': { borderColor: '#667eea' }
                  },
                  '& .MuiSelect-select': {
                    color: 'rgba(255, 255, 255, 0.9)'
                  }
                }}
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      sx: {
                        bgcolor: 'rgba(30, 30, 46, 0.98)',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        '& .MuiMenuItem-root': {
                          color: 'rgba(255, 255, 255, 0.9)',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)'
                          },
                          '&.Mui-selected': {
                            backgroundColor: 'rgba(102, 126, 234, 0.3)',
                            '&:hover': {
                              backgroundColor: 'rgba(102, 126, 234, 0.4)'
                            }
                          }
                        }
                      }
                    }
                  }
                }}
              >
                <MenuItem value="">
                  {availableClients.length === 0 
                    ? '-- No clients available (all have appointments) --' 
                    : '-- Select a client --'}
                </MenuItem>
                {availableClients.map((client) => (
                  <MenuItem key={client._id} value={client._id}>
                    {client.firstName} {client.lastName}
                  </MenuItem>
                ))}
              </TextField>
              {availableClients.length === 0 && (
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', mt: 1, display: 'block' }}>
                  All clients already have appointments. Subsequent appointments are managed through session notes.
                </Typography>
              )}
            </Grid>
            <Grid item xs={12}>
              <div style={{ position: 'relative', zIndex: 1000 }}>
                <label className="block text-sm font-medium theme-text-secondary mb-2">Appointment Date/Time *</label>
                <DatePicker
                  selected={newAppointment.scheduledStart}
                  onChange={(date: Date | null) => {
                    if (date) {
                      setNewAppointment({
                        ...newAppointment,
                        scheduledStart: date
                      });
                    }
                  }}
                  showTimeSelect
                  dateFormat="dd/MM/yyyy HH:mm"
                  timeIntervals={15}
                  className="mt-1 block w-full p-3 border border-white/20 rounded-lg bg-white/5 text-white focus:border-purple-500 focus:ring-2 focus:ring-purple-500/50 transition-all"
                  placeholderText="Select date and time"
                  portalId="root"
                />
                <Typography variant="caption" className="theme-text-secondary" sx={{ mt: 1, display: 'block' }}>
                  Appointment duration: 1 hour
                </Typography>
              </div>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={() => setIsCreatingAppointment(false)}
            sx={{ textTransform: 'none', color: 'rgba(255, 255, 255, 0.6)' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateAppointment}
            variant="contained"
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              backgroundColor: '#667eea',
              '&:hover': { backgroundColor: '#5568d3' }
            }}
          >
            Create Appointment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
        </div>
      </div>
      </Box>
    </ThemeBackground>
  );
};

export default AppointmentDashboard;
